// src/App.js
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Lazy load pages
const HomePage = lazy(() => import('./pages/HomePage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const FaceLivenessStep = lazy(() => import('./pages/FaceLivenessStep'));
const EducationHub = lazy(() => import('./pages/EducationHub'));
const TradeMarketplace = lazy(() => import('./pages/TradeMarketplace'));
const OrderTracking = lazy(() => import('./pages/OrderTracking'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const Settings = lazy(() => import('./pages/Settings')); 
// FIX 1: Added the missing MarketplaceCreate import
const MarketplaceCreate = lazy(() => import('./pages/MarketplaceCreate'));
const ProductListing = lazy(() => import('./pages/ProductListing'));
// NEW: Added the Order Checkout page for Paystack Escrow
const OrderCheckout = lazy(() => import('./pages/OrderCheckout'));

const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-screen bg-emerald-50 dark:bg-slate-900">
    <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

function PrivateRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function RedirectIfAuth({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  return isAuthenticated ? <Navigate to="/dashboard" /> : children;
}

export default function App() {
  return (
    <div className="min-h-screen w-screen flex flex-col bg-emerald-50 dark:bg-slate-900 overflow-x-hidden">
      <Navbar />
      <main className="flex-grow w-full">
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<RedirectIfAuth><LoginPage /></RedirectIfAuth>} />
            <Route path="/signup" element={<RedirectIfAuth><SignupPage /></RedirectIfAuth>} />

            <Route path="/verify-face" element={<FaceLivenessStep mode="forgot_password" />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Protected Routes */}
            <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
            
            <Route path="/kyc" element={<PrivateRoute><FaceLivenessStep mode="KYC" /></PrivateRoute>} />
            <Route path="/kyc-verification" element={<PrivateRoute><FaceLivenessStep mode="KYC" /></PrivateRoute>} />
            
            <Route path="/marketplace" element={<PrivateRoute><TradeMarketplace /></PrivateRoute>} />
            
            {/* FIX 2: Added the specific route for Marketplace Creation */}
            <Route path="/marketplace/create" element={<PrivateRoute><MarketplaceCreate /></PrivateRoute>} />
            
            {/* NEW: Route for the Escrow Payment Generation */}
            <Route path="/orders/create" element={<PrivateRoute><OrderCheckout /></PrivateRoute>} />
            
            {/* FIX 3: Added plural alias for Order Tracking to prevent blank page if URL differs */}
            <Route path="/orders" element={<PrivateRoute><OrderTracking /></PrivateRoute>} />
            <Route path="/order-tracking" element={<PrivateRoute><OrderTracking /></PrivateRoute>} />
            
            <Route path="/list-product" element={<PrivateRoute><ProductListing /></PrivateRoute>} />
            <Route path="/admin" element={<PrivateRoute><AdminPanel /></PrivateRoute>} />

            <Route path="/education-hub" element={<EducationHub />} />

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}